/**
 * Static descriptors for every distinct sync phase the orchestrator emits.
 *
 * The `name` field is the exact `phase` string carried by SSE events
 * (`phase_start`, `progress`, `phase_end`, `phase_skipped`). Add an entry
 * here whenever a new `_Phase(step, name, ...)` is introduced in
 * `api/sync.py`. Order here drives the order of the UI list.
 *
 * `step` matches the orchestrator's step number (some steps emit multiple
 * named events, e.g. step 5 splits into `fetch_categories` +
 * `fetch_catalogs`; step 6 splits into three).
 */
export type PhaseDescriptor = {
  name: string;
  step: number;
  title: string;
  description: string;
  /** ServiceNow tables this phase pulls from (display only). */
  snTables: string[];
  /** Snapshot tables this phase writes to. Used to look up last-known counts. */
  snapshotTables: string[];
};

export const PHASES: PhaseDescriptor[] = [
  {
    name: 'clear_staging',
    step: 1,
    title: 'Prepare staging',
    description:
      'Wipes every *_staging table. With force_full=false, also seeds staging from live so cached/skipped phases inherit last-good data.',
    snTables: [],
    snapshotTables: [],
  },
  {
    name: 'fetch_kb_index',
    step: 2,
    title: 'KB index',
    description:
      'Crawls the KB0053029 tree to discover the streams/categories/forms that scope the rest of the sync. Cheap.',
    snTables: ['kb_knowledge'],
    snapshotTables: ['streams', 'kb_index'],
  },
  {
    name: 'fetch_kb_articles',
    step: 3,
    title: 'KB articles',
    description:
      'Fetches every KB body discovered in the index. Parallelised x3 — the rate limiter caps total throughput.',
    snTables: ['kb_knowledge'],
    snapshotTables: ['kb_articles'],
  },
  {
    name: 'fetch_record_producers',
    step: 4,
    title: 'Record producers',
    description:
      'Catalog item producers (the "form" header) for HRSD + DBS scopes — name, table, category, assignment group.',
    snTables: ['sc_cat_item_producer'],
    snapshotTables: ['sn_record_producers'],
  },
  {
    name: 'fetch_categories',
    step: 5,
    title: 'Catalog categories',
    description: 'Whole `sc_category` table — small.',
    snTables: ['sc_category'],
    snapshotTables: ['sn_categories'],
  },
  {
    name: 'fetch_catalogs',
    step: 5,
    title: 'Catalogs',
    description: 'Whole `sc_catalog` table — small.',
    snTables: ['sc_catalog'],
    snapshotTables: ['sn_catalogs'],
  },
  {
    name: 'fetch_variable_sets',
    step: 6,
    title: 'Variable sets',
    description: 'Headers for variable-set containers (`item_option_new_set`).',
    snTables: ['item_option_new_set'],
    snapshotTables: ['sn_variable_sets'],
  },
  {
    name: 'fetch_variable_set_membership',
    step: 6,
    title: 'Variable set membership',
    description: 'M2M between cat_items and variable sets (whole table).',
    snTables: ['io_set_item'],
    snapshotTables: ['sn_variable_set_membership'],
  },
  {
    name: 'fetch_variables',
    step: 6,
    title: 'Variables (form fields)',
    description:
      'Form fields — whole `item_option_new` table. Often the largest fetch in the run; cached for 48h.',
    snTables: ['item_option_new'],
    snapshotTables: ['sn_variables'],
  },
  {
    name: 'fetch_choices',
    step: 7,
    title: 'Question choices',
    description:
      'Dropdown values per variable — whole `question_choice` table.',
    snTables: ['question_choice'],
    snapshotTables: ['sn_question_choices'],
  },
  {
    name: 'fetch_catalog_client_scripts',
    step: 8,
    title: 'Catalog client scripts',
    description:
      'OnLoad / onChange catalog client scripts attached to in-scope producers and variable sets — used to detect country-gated visibility and dynamic choice lists.',
    snTables: ['catalog_script_client'],
    snapshotTables: ['sn_catalog_client_scripts'],
  },
  {
    name: 'fetch_catalog_ui_policies',
    step: 8,
    title: 'Catalog UI policies',
    description:
      'UI policies attached to in-scope producers and variable sets.',
    snTables: ['catalog_ui_policy'],
    snapshotTables: ['sn_catalog_ui_policies'],
  },
  {
    name: 'fetch_catalog_ui_policy_actions',
    step: 8,
    title: 'UI policy actions',
    description:
      'Per-policy field actions (visible / mandatory / read-only).',
    snTables: ['catalog_ui_policy_action'],
    snapshotTables: ['sn_catalog_ui_policy_actions'],
  },
  {
    name: 'fetch_routing_rules',
    step: 9,
    title: 'Routing rules',
    description:
      'Per-stream/per-country routing assignments (`sysrule_assignment`).',
    snTables: ['sysrule_assignment'],
    snapshotTables: ['sn_routing_rules'],
  },
  {
    name: 'fetch_slas',
    step: 10,
    title: 'SLAs',
    description:
      'Generic `contract_sla` plus area-specific SLA tables (`u_p2p_request_sla`, etc.). Each area runs as its own sub-phase.',
    snTables: ['contract_sla', 'u_*_request_sla'],
    snapshotTables: ['sn_slas'],
  },
  {
    name: 'fetch_producer_assignment_groups',
    step: 11,
    title: 'Producer ↔ assignment-group counts',
    description:
      'Stats API aggregate over each area\'s requests table — discovers which assignment groups actually handle each producer\'s tickets. Parallelised across areas. Output drives the AG scoping for the next three phases.',
    snTables: ['x_grda_*_requests'],
    snapshotTables: ['sn_producer_assignment_groups'],
  },
  {
    name: 'fetch_user_groups',
    step: 12,
    title: 'Assignment groups',
    description:
      'Scoped to AGs referenced by producers, routing rules, or the Stats-API discovery — typically a few thousand instead of the full 14K-row `sys_user_group` table.',
    snTables: ['sys_user_group'],
    snapshotTables: ['sn_user_groups'],
  },
  {
    name: 'fetch_group_members',
    step: 13,
    title: 'Group members',
    description:
      'Memberships scoped to the same AG set. Drops the unscoped 280K-row pull by an order of magnitude.',
    snTables: ['sys_user_grmember'],
    snapshotTables: ['sn_user_group_members'],
  },
  {
    name: 'fetch_users',
    step: 14,
    title: 'Users',
    description:
      'User profiles for the members surfaced above — scoped from the unscoped 310K-row pull.',
    snTables: ['sys_user'],
    snapshotTables: ['sn_users'],
  },
  {
    name: 'fetch_item_availability',
    step: 15,
    title: 'Country availability',
    description:
      'Per-cat_item country/criteria gates — whole table.',
    snTables: ['sc_cat_item_user_criteria_no_mtom'],
    snapshotTables: ['sn_item_availability'],
  },
  {
    name: 'derive_field_kind',
    step: 16,
    title: 'Derive field_kind',
    description:
      'Tags every variable as `choice_list` / `reference_db` / `reference_other` / `text` based on type + choice presence.',
    snTables: [],
    snapshotTables: ['sn_variables'],
  },
  {
    name: 'derive_kb_links',
    step: 17,
    title: 'Derive KB ↔ producer links',
    description:
      'Parses `kb_articles.structured_json` → `sn_record_producers.kb_number`. Optional `data/manual_kb_links.csv` patches misses.',
    snTables: [],
    snapshotTables: ['sn_record_producers'],
  },
  {
    name: 'derive_field_dynamics',
    step: 18,
    title: 'Derive field dynamics',
    description:
      'Parses catalog client scripts and UI policies into per-field localization rows — drives the universal vs country-specific split in the form view.',
    snTables: [],
    snapshotTables: ['sn_field_dynamics'],
  },
  {
    name: 'fk_check',
    step: 19,
    title: 'FK sanity check',
    description:
      'Scans staging FK relationships for orphans (e.g. variables pointing at deleted producers). Warn-only — produces fk_check_summary on the SSE feed.',
    snTables: [],
    snapshotTables: [],
  },
  {
    name: 'atomic_swap',
    step: 20,
    title: 'Atomic swap',
    description:
      'Single transaction: TRUNCATE live, INSERT live FROM staging, TRUNCATE staging. Readers continue to see the previous snapshot.',
    snTables: [],
    snapshotTables: [],
  },
  {
    name: 'set_data_mode_offline',
    step: 21,
    title: 'Switch to offline',
    description: 'Flips `data_mode` to `offline` so the snapshot is the source of truth.',
    snTables: [],
    snapshotTables: [],
  },
  {
    name: 'finish_job',
    step: 22,
    title: 'Finish',
    description: 'Final summary event.',
    snTables: [],
    snapshotTables: [],
  },
];

const PHASE_BY_NAME: Record<string, PhaseDescriptor> = Object.fromEntries(
  PHASES.map((p) => [p.name, p]),
);

/**
 * Resolve an SSE phase string to its descriptor.
 *
 * Handles dynamic area-SLA sub-phases (`fetch_slas[P2P]` → fetch_slas) by
 * stripping any `[area]` suffix.
 */
export function describePhase(phaseName: string): PhaseDescriptor | null {
  if (PHASE_BY_NAME[phaseName]) return PHASE_BY_NAME[phaseName];
  const subMatch = /^([a-z_]+)\[/.exec(phaseName);
  if (subMatch && PHASE_BY_NAME[subMatch[1]]) return PHASE_BY_NAME[subMatch[1]];
  return null;
}
